import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from "react";
import {
  ClipboardPaste,
  X,
  ArrowUp,
  List,
  Sun,
  Moon,
  Check,
  AlertCircle,
  Maximize2,
  Minimize2,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  FileDown,
  FileCode,
  MoreVertical,
  FolderOpen,
  FileUp,
} from "lucide-react";
import { MarkdownRenderer } from "./markdown-renderer";


// ── Types ──

interface TOCItem {
  level: number;
  text: string;
  id: string;
}

interface TOCNode {
  item: TOCItem;
  index: number;
  children: TOCNode[];
}

function buildTOCTree(items: TOCItem[]): TOCNode[] {
  const roots: TOCNode[] = [];
  const stack: TOCNode[] = [];

  items.forEach((item, index) => {
    const node: TOCNode = { item, index, children: [] };
    while (stack.length > 0 && stack[stack.length - 1].item.level >= item.level) {
      stack.pop();
    }
    if (stack.length === 0) {
      roots.push(node);
    } else {
      stack[stack.length - 1].children.push(node);
    }
    stack.push(node);
  });

  return roots;
}

function collectDescendantIds(nodes: TOCNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    ids.push(node.item.id);
    if (node.children.length > 0) {
      ids.push(...collectDescendantIds(node.children));
    }
  }
  return ids;
}

type ViewMode = "standard" | "full";



// ── TOC extraction ──

function extractTOC(content: string): TOCItem[] {
  const items: TOCItem[] = [];
  const lines = content.split("\n");
  let inCodeBlock = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("```") || trimmed.startsWith("~~~")) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;

    const match = line.match(/^(#{1,3})\s+(.+)$/);
    if (match) {
      const text = match[2].replace(/[*_`[\]]/g, "").trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      items.push({ level: match[1].length, text, id });
    }
  }
  return items;
}

// ── Reading time ──

function getReadingStats(content: string): { words: number; minutes: number } {
  const words = content
    .replace(/```[\s\S]*?```/g, "")
    .replace(/[#*_`[\]()>|!-]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 0).length;
  return { words, minutes: Math.max(1, Math.ceil(words / 200)) };
}


// ── Main Component ──

export function MarkdownReaderPage() {
  const [content, setContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("Untitled");
  const [isDragging, setIsDragging] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteValue, setPasteValue] = useState("");
  const [showTOC, setShowTOC] = useState(false);
  const [collapsedTOC, setCollapsedTOC] = useState<Record<string, boolean>>({});
  const [tocClosing, setTocClosing] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string>("");
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("mojoReader-theme");
    if (saved !== null) return saved === "dark";
    return false; // Default to light mode
  });
  const [viewMode, setViewMode] = useState<ViewMode>("standard");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Update PWA title bar color to match current theme
  const updateThemeColor = useCallback((isDark: boolean) => {
    const color = isDark ? "#121316" : "#f9fafb";
    document
      .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
      .forEach((meta) => {
        meta.setAttribute("content", color);
      });
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const backToTopRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);
    const scrollSpyObserverRef = useRef<IntersectionObserver | null>(null);
  const isNavigatingRef = useRef(false);
  const isUserInteractingWithTOCRef = useRef(false);
  const tocNavRef = useRef<HTMLElement | null>(null);
  const tocInteractionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const windowIdRef = useRef<string>(Math.random().toString(36).substring(2, 9));
  const fileNameRef = useRef<string>(fileName);
  const dragCounterRef = useRef<number>(0);

  useEffect(() => {
    fileNameRef.current = fileName;
  }, [fileName]);

  // Cross-window messaging: listen for duplicate file queries and focus existing window
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel("mojoReader_file_tracker");

    channel.onmessage = (event: MessageEvent) => {
      const { type, fileName: targetFile, senderId } = event.data || {};
      if (senderId === windowIdRef.current) return;

      if (type === "QUERY_FILE_OPEN") {
        if (fileNameRef.current && fileNameRef.current === targetFile) {
          channel.postMessage({
            type: "FILE_IS_OPEN",
            fileName: targetFile,
            responderId: windowIdRef.current,
          });
          window.focus();
        }
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  // Apply saved dark mode preference and handle PWA file launch on mount
  useEffect(() => {
    // Apply saved dark mode preference on mount
    const saved = localStorage.getItem("mojoReader-theme");
    if (saved !== null) {
      const isDark = saved === "dark";
      document.documentElement.classList.toggle("dark", isDark);
      setDarkMode(isDark);
      updateThemeColor(isDark);
    } else {
      // Default to light mode
      document.documentElement.classList.remove("dark");
      setDarkMode(false);
      updateThemeColor(false);
    }

    // Handle OS file association (PWA File Handling API).
    // When the user double-clicks a .md file and mojoReader is the handler,
    // the OS launches the PWA and the file is delivered via launchQueue.
    if ("launchQueue" in window) {
      (window as { launchQueue: { setConsumer: (cb: (params: { files: { getFile: () => Promise<File> }[] }) => void) => void } }).launchQueue.setConsumer(
        async (launchParams) => {
          const files = launchParams.files;
          if (!files || files.length === 0) return;
          try {
            const handle = files[0];
            const file = await handle.getFile();

            // Check if document is already open in an existing window/tab
            if (typeof BroadcastChannel !== "undefined") {
              const isAlreadyOpen = await new Promise<boolean>((resolve) => {
                const channel = new BroadcastChannel("mojoReader_file_tracker");
                let resolved = false;

                const timer = setTimeout(() => {
                  if (!resolved) {
                    resolved = true;
                    channel.close();
                    resolve(false);
                  }
                }, 150);

                channel.onmessage = (event: MessageEvent) => {
                  const { type, fileName: responderFile } = event.data || {};
                  if (type === "FILE_IS_OPEN" && responderFile === file.name) {
                    if (!resolved) {
                      resolved = true;
                      clearTimeout(timer);
                      channel.close();
                      resolve(true);
                    }
                  }
                };

                channel.postMessage({
                  type: "QUERY_FILE_OPEN",
                  fileName: file.name,
                  senderId: windowIdRef.current,
                });
              });

              if (isAlreadyOpen) {
                // Existing window was focused! Close duplicate launch window if permitted
                try {
                  window.close();
                } catch {
                  // Ignore if browser prevents closing window
                }
                return;
              }
            }

            const text = await file.text();
            setContent(text);
            setFileName(file.name);
            window.scrollTo(0, 0);
            if (window.location.hash) {
              history.replaceState(null, "", window.location.pathname);
            }
          } catch (err) {
            console.warn('mojoReader: Failed to read file via launchQueue (PWA file handler)', err);
          }
        }
      );
    }
  }, [updateThemeColor]);

  // Scroll tracking — uses direct DOM manipulation to avoid re-rendering
  // the entire component tree (which causes mermaid diagrams to flicker)
  useEffect(() => {
    if (!content) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;

      // Update progress bar width directly via DOM
      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${progress * 100}%`;
      }
      // Show/hide back-to-top button directly via DOM
      if (backToTopRef.current) {
        backToTopRef.current.style.display = scrollTop > 400 ? "flex" : "none";
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [content]);

  // Update browser tab title to match the open document.
  // This also sets the default filename when printing to PDF.
  useEffect(() => {
    const defaultTitle = "mojoReader — Markdown Reader by Business Mojo";
    if (content && fileName) {
      const cleanName = fileName.replace(/\.(md|markdown|txt|mdx)$/i, "");
      document.title = `${cleanName} — mojoReader`;
    } else {
      document.title = defaultTitle;
    }
    return () => {
      document.title = defaultTitle;
    };
  }, [content, fileName]);

  // Close kebab menu on click outside or Escape key
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  // Auto-revert full-width mode when viewport drops below 1200px
  // (the toggle button is hidden at that breakpoint via CSS)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200 && viewMode === "full") {
        setViewMode("standard");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [viewMode]);

  const toggleDarkMode = useCallback(() => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    document.documentElement.classList.toggle("dark", newMode);
    localStorage.setItem("mojoReader-theme", newMode ? "dark" : "light");
    updateThemeColor(newMode);
  }, [darkMode, updateThemeColor]);


  // File handling
  // ── Toast state ──
  const [toast, setToast] = useState<{ title: string; message?: string; icon?: ReactNode } | null>(null);
  const [toastExiting, setToastExiting] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(null);

  const dismissToast = useCallback(() => {
    setToastExiting(true);
    setTimeout(() => {
      setToast(null);
      setToastExiting(false);
    }, 250); // matches toast-out animation duration
  }, []);

  const showToast = useCallback((title: string, message?: string, icon?: ReactNode) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastExiting(false);
    setToast({ title, message, icon });
    toastTimer.current = setTimeout(() => dismissToast(), 5000);
  }, [dismissToast]);

  const handleFile = useCallback((file: File) => {
    const allowedExtensions = ['.md', '.markdown', '.txt', '.mdx'];
    const fileExt = file.name.includes('.')
      ? '.' + file.name.split('.').pop()!.toLowerCase()
      : '';
    if (!allowedExtensions.includes(fileExt)) {
      showToast(
        'Unsupported file type',
        `"${file.name}" can't be opened. Try .md, .markdown, .txt, or .mdx.`,
        <AlertCircle className="h-5 w-5" />,
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setContent(text);
      setFileName(file.name);
      window.scrollTo(0, 0);
      if (window.location.hash) {
        history.replaceState(null, "", window.location.pathname);
      }
    };
    reader.onerror = () => {
      console.warn('mojoReader: FileReader failed to read file', reader.error);
      showToast(
        'Failed to read file',
        `"${file.name}" may be corrupt or unreadable.`,
        <AlertCircle className="h-5 w-5" />,
      );
    };
    reader.readAsText(file);
  }, [showToast]);

  // Drag & drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  // Paste handling
  const handlePasteSubmit = useCallback(() => {
    if (!pasteValue.trim()) return;
    setContent(pasteValue);
    const firstHeading = pasteValue.match(/^#\s+(.+)$/m);
    const name = firstHeading ? firstHeading[1] : "Pasted Content";
    setFileName(name);
    window.scrollTo(0, 0);
    setShowPaste(false);
    setPasteValue("");
  }, [pasteValue]);

  // TOC closing animation helper
  const closeTOC = useCallback(() => {
    setTocClosing(true);
    setTimeout(() => {
      setShowTOC(false);
      setTocClosing(false);
    }, 250);
  }, []);


  const handleDownloadMd = useCallback(() => {
    if (!content) return;
    try {
      const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Use the current fileName, ensuring it has a .md extension
      const downloadName = fileName.match(/\.(md|markdown|txt|mdx)$/i)
        ? fileName
        : `${fileName}.md`;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setIsMenuOpen(false);
    } catch (err) {
      console.warn("mojoReader: Failed to download markdown file", err);
    }
  }, [content, fileName]);

  const handleCopyMarkdown = useCallback(async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
      setIsMenuOpen(false);
    } catch (err) {
      console.warn("mojoReader: Failed to copy markdown to clipboard", err);
    }
  }, [content]);

  const handleDownloadHtml = useCallback(async () => {
    try {
      // 1. Get the rendered content element
      const contentEl = document.querySelector("#main-content .reader-markdown");
      if (!contentEl) return;

      // 2. Clone the rendered content
      const clone = contentEl.cloneNode(true) as HTMLElement;

      // 2b. Strip interactive elements that won't work in static HTML
      clone.querySelectorAll(".code-block-copy-btn, .table-copy-btn")
        .forEach(el => el.remove());

      // 3. Collect relevant CSS
      // We need: globals.css variables, markdown.css styles, katex CSS
      const stylesheets = Array.from(document.styleSheets);
      let collectedCSS = "";
      
      for (const sheet of stylesheets) {
        try {
          const rules = Array.from(sheet.cssRules);
          for (const rule of rules) {
            const ruleText = rule.cssText;
            // Include CSS custom properties (:root and .dark), markdown-preview styles, 
            // katex styles, mermaid styles, and callout styles.
            // Skip reader-* styles (UI chrome) and @import rules
            if (
              ruleText.includes(":root") ||
              ruleText.includes(".dark") ||
              ruleText.includes(".markdown-preview") ||
              ruleText.includes(".katex") ||
              ruleText.includes(".mermaid") ||
              ruleText.includes(".callout") ||
              ruleText.includes("code") ||
              ruleText.includes("pre") ||
              ruleText.includes("blockquote") ||
              ruleText.includes("table")
            ) {
              collectedCSS += ruleText + "\n";
            }
          }
        } catch {
          // Cross-origin stylesheets (e.g., Google Fonts) may throw
          // Try to include via link tag instead
          if (sheet.href) {
            collectedCSS += `/* External: ${sheet.href} */\n`;
          }
        }
      }

      // 4. Determine current theme
      const isDark = document.documentElement.classList.contains("dark");

      // 5. Build the standalone HTML document
      const htmlContent = `<!DOCTYPE html>
<html lang="en"${isDark ? ' class="dark"' : ''}>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${fileName.replace(/\.(md|markdown|txt|mdx)$/i, "")} — mojoReader</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@200;300;400;600;700;800&display=swap" rel="stylesheet">
  <style>
    /* Reset */
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif;
      line-height: 1.6;
      padding: 2rem;
      max-width: 1024px;
      margin: 0 auto;
      background: ${isDark ? '#121316' : '#ffffff'};
      color: ${isDark ? '#e0e0e0' : '#1a1a1a'};
    }
    /* Collected styles */
    ${collectedCSS}
    /* Override interactive cursors — exported HTML has no zoom/lightbox JS */
    .markdown-preview .md-image-wrapper,
    .markdown-preview .md-expandable {
      cursor: default !important;
    }
    /* Footer */
    .mojoreader-export-footer {
      margin-top: 3rem;
      padding-top: 1.5rem;
      border-top: 1px solid ${isDark ? '#2a2a2e' : '#e0e0e0'};
      text-align: center;
      font-size: 12px;
      color: ${isDark ? '#666' : '#999'};
    }
    .mojoreader-export-footer a {
      color: ${isDark ? '#888' : '#666'};
      text-decoration: none;
    }
    .mojoreader-export-footer a:hover {
      text-decoration: underline;
    }
    @media print {
      body { background: white; color: black; }
      .mojoreader-export-footer { display: none; }
    }
  </style>
</head>
<body>
  ${clone.outerHTML}
  <footer class="mojoreader-export-footer">
    Rendered by <a href="https://reader.businessmojo.com.au">mojoReader</a> — Powered by <a href="https://businessmojo.com.au">Business Mojo</a>
  </footer>
</body>
</html>`;

      // 6. Create and trigger download
      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const baseName = fileName.replace(/\.(md|markdown|txt|mdx)$/i, "");
      a.download = `${baseName}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setIsMenuOpen(false);
    } catch (err) {
      console.warn("mojoReader: Failed to export HTML", err);
    }
  }, [fileName]);

  // Scroll to heading — suppresses scroll-spy during the smooth-scroll
  // animation so the TOC highlight doesn't flicker through intermediate headings.
  const scrollToHeading = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      // Immediately set the target heading as active
      setActiveHeadingId(id);
      // Suppress scroll-spy until the scroll settles
      isNavigatingRef.current = true;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Re-enable after the smooth scroll completes (~400-600ms)
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 600);
      // Close TOC on mobile only — desktop sidebar stays open
      if (window.innerWidth < 1024) closeTOC();
    }
  }, [closeTOC]);

  // Toggle collapse/expand with cascade
  const toggleNodeRecursive = useCallback((node: TOCNode, shouldCollapse: boolean) => {
    if (shouldCollapse) {
      const descendantIds = collectDescendantIds(node.children);
      setCollapsedTOC((prev) => {
        const next = { ...prev, [node.item.id]: true };
        for (const id of descendantIds) {
          next[id] = true;
        }
        return next;
      });
    } else {
      setCollapsedTOC((prev) => {
        const next = { ...prev };
        delete next[node.item.id];
        return next;
      });
    }
  }, []);

  // ── Compute reading-view data (only when content is present) ──
  const toc = useMemo(() => content ? extractTOC(content) : [], [content]);
  const stats = useMemo(() => content ? getReadingStats(content) : { words: 0, minutes: 0 }, [content]);

  // Build TOC tree and derive collapse-all state
  const tocTree = buildTOCTree(toc);
  const collapsibleIds = collectDescendantIds(tocTree).filter((id) => {
    const findNode = (nodes: TOCNode[]): TOCNode | undefined => {
      for (const n of nodes) {
        if (n.item.id === id) return n;
        const found = findNode(n.children);
        if (found) return found;
      }
      return undefined;
    };
    const node = findNode(tocTree);
    return node && node.children.length > 0;
  });
  const allCollapsed = collapsibleIds.length > 0 && collapsibleIds.every((id) => !!collapsedTOC[id]);

  const collapseAll = useCallback(() => {
    const tree = buildTOCTree(toc);
    const findCollapsible = (nodes: TOCNode[]): string[] => {
      const result: string[] = [];
      for (const n of nodes) {
        if (n.children.length > 0) {
          result.push(n.item.id);
          result.push(...findCollapsible(n.children));
        }
      }
      return result;
    };
    const collapsible = findCollapsible(tree);

    if (allCollapsed) {
      setCollapsedTOC({});
    } else {
      const next: Record<string, boolean> = {};
      for (const id of collapsible) {
        next[id] = true;
      }
      setCollapsedTOC(next);
    }
  }, [toc, allCollapsed]);

  // Recursive TOC node renderer
  function renderTOCNode(node: TOCNode): ReactNode {
    const { item, index, children } = node;
    const hasChildren = children.length > 0;
    const isCollapsed = !!collapsedTOC[item.id];
    const isActive = activeHeadingId === item.id;

    return (
      <div key={`${item.id}-${index}`} className={`reader-toc-node${isActive ? " reader-toc-active" : ""}`}>
        <div className="reader-toc-row">
          <button
            onClick={() => scrollToHeading(item.id)}
            className="reader-toc-item"
            style={{ paddingLeft: `${(item.level - 1) * 16 + 16}px` }}
          >
            <span
              className={`reader-toc-text ${
                item.level === 1
                  ? "toc-h1"
                  : item.level === 2
                    ? "toc-h2"
                    : "toc-h3"
              }`}
            >
              {item.text}
            </span>
            {hasChildren && (
              <span
                className="reader-toc-chevron"
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNodeRecursive(node, !isCollapsed);
                }}
                aria-label={isCollapsed ? "Expand section" : "Collapse section"}
              >
                <ChevronRight
                  className={`reader-toc-chevron-icon ${
                    !isCollapsed ? "reader-toc-chevron-icon--expanded" : ""
                  }`}
                />
              </span>
            )}
          </button>
        </div>
        {hasChildren && (
          <div
            className={`reader-toc-children ${
              isCollapsed ? "reader-toc-children--collapsed" : ""
            }`}
          >
            {children.map(renderTOCNode)}
          </div>
        )}
      </div>
    );
  }

  // Measure header height for TOC positioning
  // Depends on `content` because the header only renders when content is non-null.
  // Without this, the effect runs on mount (content === null), finds no header,
  // and never sets --header-height — causing the TOC to use the 48px fallback.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const update = () => {
      document.documentElement.style.setProperty(
        "--header-height",
        `${header.offsetHeight}px`
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    return () => ro.disconnect();
  }, [content]);

  // Scroll-spy: hybrid approach using IntersectionObserver + scroll-settle handler.
  // IntersectionObserver gives smooth real-time tracking during slow/moderate scroll.
  // The scroll-settle handler fires after scrolling stops and does a direct geometric
  // scan to find the correct heading — this catches up quickly after fast scrolling
  // where the observer may miss headings that flew through the observation zone.
  useEffect(() => {
    if (!content || toc.length === 0) return;

    // Disconnect any previous observer before creating a new one
    scrollSpyObserverRef.current?.disconnect();

    // Collect heading elements once (skip h1 — document title)
    const headingEls = toc
      .filter((h: TOCItem) => h.level > 1)
      .map((h: TOCItem) => document.getElementById(h.id))
      .filter((el: HTMLElement | null): el is HTMLElement => el !== null);

    // Direct geometric scan: find the heading nearest to (but above) the top of
    // the viewport, accounting for the fixed header offset.
    const findActiveHeading = (): string | null => {
      const headerOffset = 56; // header height + small buffer
      let bestId: string | null = null;
      let bestDistance = Infinity;

      for (const el of headingEls) {
        const top = el.getBoundingClientRect().top;
        // Heading is "active" if it's scrolled past the header but still the closest
        // one above the viewport top (or the first one below if none are above).
        const distance = top - headerOffset;
        if (distance <= 0 && Math.abs(distance) < bestDistance) {
          bestDistance = Math.abs(distance);
          bestId = el.id;
        }
      }

      // If no heading has scrolled past the header, pick the first visible one
      if (!bestId) {
        for (const el of headingEls) {
          if (el.getBoundingClientRect().top >= 0) {
            bestId = el.id;
            break;
          }
        }
      }

      return bestId;
    };

    // Scroll-settle handler: fires 150ms after scrolling stops
    const handleScroll = () => {
      if (isNavigatingRef.current) return;
      if (scrollSettleTimerRef.current) {
        clearTimeout(scrollSettleTimerRef.current);
      }
      scrollSettleTimerRef.current = setTimeout(() => {
        if (isNavigatingRef.current) return;
        const id = findActiveHeading();
        if (id) setActiveHeadingId(id);
        scrollSettleTimerRef.current = null;
      }, 150);
    };

    const timer = setTimeout(() => {
      // IntersectionObserver for real-time tracking during slow/moderate scroll
      const observer = new IntersectionObserver(
        (entries) => {
          if (isNavigatingRef.current) return;
          const visibleEntries = entries.filter((e) => e.isIntersecting);
          if (visibleEntries.length > 0) {
            visibleEntries.sort(
              (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
            );
            setActiveHeadingId(visibleEntries[0].target.id);
          }
        },
        { rootMargin: "-48px 0px -70% 0px", threshold: 0 }
      );

      scrollSpyObserverRef.current = observer;
      headingEls.forEach((el: HTMLElement) => observer.observe(el));

      // Attach scroll-settle listener
      window.addEventListener("scroll", handleScroll, { passive: true });
    }, 400);

    return () => {
      clearTimeout(timer);
      scrollSpyObserverRef.current?.disconnect();
      scrollSpyObserverRef.current = null;
      window.removeEventListener("scroll", handleScroll);
      if (scrollSettleTimerRef.current) {
        clearTimeout(scrollSettleTimerRef.current);
        scrollSettleTimerRef.current = null;
      }
    };
  }, [content, toc]);

  // Track when the user is interacting with the TOC (pointer inside or scrolling)
  // so we can suppress the auto-scroll-into-view that would fight their scroll.
  useEffect(() => {
    const nav = tocNavRef.current;
    if (!nav) return;

    const markInteracting = () => {
      isUserInteractingWithTOCRef.current = true;
      // Clear any existing "end" timer so rapid events extend the window
      if (tocInteractionTimerRef.current) {
        clearTimeout(tocInteractionTimerRef.current);
        tocInteractionTimerRef.current = null;
      }
    };

    const markIdle = () => {
      // Small debounce so brief pointer movements don't flicker
      tocInteractionTimerRef.current = setTimeout(() => {
        isUserInteractingWithTOCRef.current = false;
        tocInteractionTimerRef.current = null;
      }, 400);
    };

    const handleScroll = () => {
      markInteracting();
      // Reset after scrolling stops
      if (tocInteractionTimerRef.current) {
        clearTimeout(tocInteractionTimerRef.current);
      }
      tocInteractionTimerRef.current = setTimeout(() => {
        isUserInteractingWithTOCRef.current = false;
        tocInteractionTimerRef.current = null;
      }, 600);
    };

    nav.addEventListener("pointerenter", markInteracting);
    nav.addEventListener("pointerleave", markIdle);
    nav.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      nav.removeEventListener("pointerenter", markInteracting);
      nav.removeEventListener("pointerleave", markIdle);
      nav.removeEventListener("scroll", handleScroll);
      if (tocInteractionTimerRef.current) {
        clearTimeout(tocInteractionTimerRef.current);
      }
    };
  }, [showTOC]);

  // Auto-scroll TOC to keep active heading visible.
  // Uses manual scrollTop on the TOC's scroll container instead of scrollIntoView,
  // because scrollIntoView scrolls ALL ancestor containers — including the window —
  // which causes the main content to jump during fast scrolling.
  // Suppressed when the user is actively interacting with the TOC.
  useEffect(() => {
    if (!activeHeadingId || !showTOC) return;
    // Don't yank the TOC scroll position while the user is browsing it
    if (isUserInteractingWithTOCRef.current) return;

    const activeItem = document.querySelector(".reader-toc-active .reader-toc-item");
    if (!activeItem) return;

    // On desktop (≥1024px), .reader-toc-sheet is the scrollable container;
    // on mobile, .reader-toc-nav is the scrollable container.
    const scrollContainer =
      activeItem.closest(".reader-toc-sheet") ?? activeItem.closest(".reader-toc-nav");
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();

    // Only scroll if the active item is outside the visible portion of the container
    if (itemRect.top >= containerRect.top && itemRect.bottom <= containerRect.bottom) return;

    // Calculate target scrollTop to center the item within the container
    const itemOffsetInContainer =
      itemRect.top - containerRect.top + scrollContainer.scrollTop;
    const targetScrollTop =
      itemOffsetInContainer - containerRect.height / 2 + itemRect.height / 2;

    scrollContainer.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });
  }, [activeHeadingId, showTOC]);

  // ── Unified render ──
  // Both views live in a single return. The landing view is always mounted
  // (hidden via display:none when reading) so switching back is instant —
  // no unmount/mount reflow that causes a visible flicker on large docs.
  return (
    <>
      {/* ── Reading View ── */}
      {content && (
        <div
          className="reader-close-wrapper"
          style={{ opacity: 1 }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
        <div className="reader-container">
        <a href="#main-content" className="reader-skip-link">Skip to content</a>
        {/* Progress bar */}
        <div className="reader-progress-track">
          <div
            ref={progressBarRef}
            className="reader-progress-bar"
            style={{ width: "0%" }}
          />
        </div>

        {/* Top bar */}
        <header className="reader-header" ref={headerRef}>
          <div className="reader-header-left">
            {toc.length > 0 && (
              <button
                onClick={() => { if (showTOC) closeTOC(); else setShowTOC(true); }}
                className="reader-header-btn"
                title="Table of contents"
                aria-label={showTOC ? "Close table of contents" : "Open table of contents"}
              >
                <List className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="reader-header-btn reader-open-file-btn"
              title="Open file"
              aria-label="Open file"
            >
              <FolderOpen className="h-5 w-5" />
            </button>
            <div className="reader-header-title">
              <h1 className="reader-doc-title">{fileName.replace(/\.(md|markdown|txt|mdx)$/i, "")}</h1>
              <span className="reader-doc-meta">
                {stats.words.toLocaleString()} words · {stats.minutes} min read
              </span>
            </div>
          </div>

          <div className="reader-header-actions">
            <button
              onClick={() => setViewMode(viewMode === "standard" ? "full" : "standard")}
              className="reader-header-btn reader-full-width-btn"
              title={viewMode === "standard" ? "Full width" : "Standard width"}
              aria-label={viewMode === "standard" ? "Switch to full width" : "Switch to standard width"}
            >
              {viewMode === "standard" ? (
                <Maximize2 className="h-4 w-4" />
              ) : (
                <Minimize2 className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={toggleDarkMode}
              className="reader-header-btn reader-dark-mode-btn"
              title="Toggle dark mode"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            <div className="reader-menu-wrapper" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="reader-header-btn reader-kebab-btn"
                title="More actions"
                aria-label="More actions"
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {isMenuOpen && (
                <div className="reader-menu" role="menu">
                  <button
                    className="reader-menu-item reader-open-file-menu"
                    onClick={() => { fileInputRef.current?.click(); setIsMenuOpen(false); }}
                    role="menuitem"
                  >
                    <FolderOpen className="h-4 w-4" />
                    <span>Open file</span>
                  </button>
                  <div className="reader-menu-divider reader-open-file-menu" role="separator" />
                  <button
                    className="reader-menu-item"
                    onClick={() => {
                      setContent(null);
                      setFileName("Untitled");
                      window.scrollTo(0, 0);
                      setShowPaste(true);
                      setIsMenuOpen(false);
                    }}
                    role="menuitem"
                  >
                    <ClipboardPaste className="h-4 w-4" />
                    <span>Paste markdown</span>
                  </button>
                  <button
                    className="reader-menu-item reader-dark-mode-menu"
                    onClick={() => { toggleDarkMode(); setIsMenuOpen(false); }}
                    role="menuitem"
                  >
                    {darkMode ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                    <span>{darkMode ? "Light mode" : "Dark mode"}</span>
                  </button>
                  <div className="reader-menu-divider" role="separator" />
                  <button
                    className="reader-menu-item"
                    onClick={handleCopyMarkdown}
                    role="menuitem"
                  >
                    {copyFeedback ? (
                      <Check className="h-4 w-4" style={{ color: "#34d399" }} />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    <span>{copyFeedback ? "Copied!" : "Copy markdown"}</span>
                  </button>
                  <button
                    className="reader-menu-item"
                    onClick={handleDownloadMd}
                    role="menuitem"
                  >
                    <FileDown className="h-4 w-4" />
                    <span>Save as .md</span>
                  </button>
                  <button
                    className="reader-menu-item"
                    onClick={handleDownloadHtml}
                    role="menuitem"
                  >
                    <FileCode className="h-4 w-4" />
                    <span>Save as .html</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Flex body: TOC sidebar (desktop) + content */}
        <div className="reader-body">
        {/* TOC — rendered before content so it becomes the left flex child on desktop */}
        {(showTOC || tocClosing) && (
          <div
            className={`reader-toc-overlay${tocClosing ? " toc-closing" : ""}`}
            onClick={() => { if (window.innerWidth < 1024) closeTOC(); }}
            role="dialog"
            aria-modal="true"
            aria-label="Table of contents"
          >
            <div
              className={`reader-toc-sheet${tocClosing ? " toc-closing" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="reader-toc-handle" />
              <div className="reader-toc-header">
                <h2 className="reader-toc-title">Contents</h2>
                <div className="reader-toc-header-actions">
                  {collapsibleIds.length > 0 && (
                    <button
                      className="reader-header-btn reader-toc-collapse-btn"
                      onClick={collapseAll}
                      title={allCollapsed ? "Expand all sections" : "Collapse all sections"}
                      aria-label={allCollapsed ? "Expand all sections" : "Collapse all sections"}
                    >
                      {allCollapsed ? <ChevronsUpDown className="h-4 w-4" /> : <ChevronsDownUp className="h-4 w-4" />}
                    </button>
                  )}
                  <button
                    className="reader-header-btn"
                    onClick={closeTOC}
                    title="Close table of contents"
                    aria-label="Close table of contents"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <nav className="reader-toc-nav" ref={tocNavRef}>
                {tocTree.map(renderTOCNode)}
              </nav>
            </div>
          </div>
        )}

        {/* Content */}
        <main id="main-content" className={`reader-content reader-content-${viewMode}`} ref={contentRef}>
          <MarkdownRenderer content={content} className="reader-markdown" />
        </main>
        </div>

        {/* Back to top FAB */}
        <button
          ref={backToTopRef}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="reader-back-to-top"
          style={{ display: "none" }}
          title="Back to top"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
        </div>
        </div>
      )}

      {/* ── Landing View ── */}
      <div
        className="reader-landing"
        style={content ? { display: "none" } : undefined}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      <a href="#main-content" className="reader-skip-link">Skip to content</a>
      <div className="reader-landing-controls">
        <button
          onClick={toggleDarkMode}
          className="reader-header-btn"
          title="Toggle dark mode"
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {darkMode ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </button>
      </div>
      <div id="main-content" className="reader-landing-inner">
        {/* Header */}
        <div className="reader-landing-header">
          <div className="reader-landing-logo">
            <img src="/logo.png" alt="mojoReader" style={{ width: 100, height: 100 }} />
          </div>
          <h1 className="reader-landing-title">mojoReader</h1>
          <p className="reader-landing-subtitle">
            Beautifully render AI-generated documents — diagrams, tables, math & more
          </p>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.markdown,.txt,.mdx"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />

        {/* Primary action */}
        <button
          className="reader-open-btn"
          onClick={() => fileInputRef.current?.click()}
        >
          <FolderOpen className="h-5 w-5" />
          Open File
        </button>

        {/* Drop hint */}
        <p className="reader-drop-hint">or drop a file anywhere</p>

        {/* Secondary action */}
        <button
          className="reader-paste-link"
          onClick={() => setShowPaste(!showPaste)}
        >
          <ClipboardPaste className="h-4 w-4" />
          Paste markdown
        </button>

        {/* Paste textarea (hidden by default) */}
        {showPaste && (
          <div className="reader-paste-panel">
            <textarea
              value={pasteValue}
              onChange={(e) => setPasteValue(e.target.value)}
              placeholder="Paste your markdown here..."
              className="reader-textarea"
              rows={8}
              autoFocus
            />
            <button
              onClick={handlePasteSubmit}
              disabled={!pasteValue.trim()}
              className="reader-submit-btn"
            >
              Render
            </button>
          </div>
        )}

        {/* Footer branding */}
        <div className="reader-footer">
          <p>
            Powered by{" "}
            <a href="https://businessmojo.com.au" target="_blank" rel="noopener noreferrer">
              Business Mojo
            </a>
            {"  ·  "}
            <a href="/terms.html" style={{ color: "var(--muted-foreground)" }}>
              Terms
            </a>
            {"  ·  "}
            <a href="/privacy.html" style={{ color: "var(--muted-foreground)" }}>
              Privacy
            </a>
            {"  ·  "}
            <span style={{ color: "var(--muted-foreground)" }}>v{__APP_VERSION__}</span>
          </p>
        </div>
      </div>

      {/* Drag overlay — full-page translucent glassmorphic overlay during active drag */}
      {isDragging && (
        <div className="reader-drag-overlay" aria-hidden="true">
          <div className="reader-drag-inset-frame" />
          <div className="reader-drag-overlay-card">
            <div className="reader-drag-icon-wrapper">
              <FileUp className="h-8 w-8" />
            </div>
            <h2 className="reader-drag-title">Drop file to open</h2>
            <p className="reader-drag-subtitle">Release your document anywhere on the screen</p>
            <div className="reader-drag-badges">
              <span className="reader-drag-badge">.md</span>
              <span className="reader-drag-badge">.markdown</span>
              <span className="reader-drag-badge">.txt</span>
              <span className="reader-drag-badge">.mdx</span>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Toast notification */}
      {toast && (
        <div className="toast-container" role="alert" aria-live="assertive">
          <div className={`toast toast-error${toastExiting ? ' toast-exit' : ''}`}>
            {toast.icon && <div className="toast-icon">{toast.icon}</div>}
            <div className="toast-message">
              <strong>{toast.title}</strong>
              {toast.message && <span>{toast.message}</span>}
            </div>
            <button
              className="toast-close"
              onClick={dismissToast}
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
